import { useEffect, useState, lazy, Suspense, startTransition } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  PurchaseBundle,
  GetUserBundles,
  SwitchBundle,
} from "../PurchasesData.ts";
import { useProfile } from "../Context/ProfileContext";
import { useBudget } from "../Context/BudgetContext";
import {
  Container,
  Grid,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from "@mui/material";
import { toast } from "react-toastify";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";

// Lazy load toast container and toast
const ToastContainer = lazy(() =>
  import("react-toastify").then((module) => ({
    default: module.ToastContainer,
  }))
);

// Lazy load components that can be deferred
const ApartmentIcon = lazy(() => import("@mui/icons-material/Apartment"));
const BusinessCenterIcon = lazy(() =>
  import("@mui/icons-material/BusinessCenter")
);
const EngineeringIcon = lazy(() => import("@mui/icons-material/Engineering"));
const AppBundleWidget = lazy(() =>
  import("../sections/@dashboard/app").then((module) => ({
    default: module.AppBundleWidget,
  }))
);
const AiAdDialog = lazy(() => import("../components/AiAdDialog"));

export const BundlesPage = () => {
  const { profile, fetchProfile } = useProfile();
  const { budget, setBudget } = useBudget();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [ownedBundles, setOwnedBundles] = useState([]);

  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState("");

  useEffect(() => {
    localStorage.setItem("isCardVisible", "true");
    localStorage.setItem("showAiAdDialog", "true");

    // Fetch user's owned bundles
    const fetchOwnedBundles = async () => {
      try {
        const response = await GetUserBundles();
        if (response.data) {
          setOwnedBundles(response.data);
        }
      } catch (error) {
        console.error("Error fetching owned bundles:", error);
      }
    };

    fetchOwnedBundles();
  }, []);

  // Helper function to get bundle info
  const getBundleInfo = (bundleType) => {
    const bundle = ownedBundles.find(
      (b) =>
        b.bundleType && b.bundleType.toLowerCase() === bundleType.toLowerCase()
    );
    return bundle;
  };

  // Handle bundle switching (no dialog, direct action)
  const handleSwitch = async (bundleType) => {
    try {
      const switchResponse = await SwitchBundle({
        UserId: profile.id,
        BundleType: bundleType,
      });

      if (switchResponse.data) {
        toast.success("Bundle switched successfully!", {
          position: "top-center",
          autoClose: 1000,
        });
        fetchProfile();

        // Refresh owned bundles
        const response = await GetUserBundles();
        if (response.data) {
          setOwnedBundles(response.data);
        }
      }
    } catch (error) {
      toast.error("Failed to switch bundle", {
        position: "top-center",
        autoClose: 1000,
      });
      if (error.redirectToLogin) {
        navigate("/Login");
      }
    }
  };

  const handlePurchase = async (ref, amount) => {
    try {
      await startTransition(async () => {
        // This should only handle purchase or renewal now (switch is handled separately)
        // Check budget
        if (budget < amount) {
          toast.error("Your budget is not enough to make this purchase!", {
            position: "top-center",
            autoClose: 1000,
          });
          return;
        }

        const response = await PurchaseBundle({
          UserId: profile.id,
          Reference: ref,
          Date: new Date(),
          Amount: amount,
          Country: profile.profile.country,
        });

        if (response.data) {
          setBudget(response.data.newAmount);

          const message =
            response.data.action === "renew"
              ? "Bundle renewed successfully!"
              : "Bundle purchased successfully!";

          toast.success(message, {
            position: "top-center",
            autoClose: 1000,
          });

          if (ref === "Student") {
            localStorage.setItem("showAiAdDialog", "true");
          }

          // Refresh owned bundles
          const bundlesResponse = await GetUserBundles();
          if (bundlesResponse.data) {
            setOwnedBundles(bundlesResponse.data);
          }
        }

        setOpenDialog(false);
        fetchProfile();
      });
    } catch (error) {
      if (error.redirectToLogin) {
        navigate("/Login");
      }
    }
  };

  return (
    <>
      <Container>
        <Helmet>
          <title> Contactie | Bundles </title>
        </Helmet>
        <Typography variant="h5" sx={{ mb: 4 }}>
          Bundles
        </Typography>


        {/* --- FREE PLAN (Horizontal Card) --- */}
        <Card
          sx={{
            mb: 5,
            p: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            boxShadow: (theme) => theme.customShadows?.z8 || 3,
            borderRadius: 2,
            background: (theme) => theme.palette.background.paper,
          }}
        >
          {/* Left Side: Header */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "200px",
              borderRight: { md: "1px solid rgba(145, 158, 171, 0.24)" },
              borderBottom: { xs: "1px solid rgba(145, 158, 171, 0.24)", md: "none" },
              pb: { xs: 2, md: 0 },
              pr: { md: 2 },
              mb: { xs: 2, md: 0 },
            }}
          >
            <EmojiPeopleIcon color="primary" sx={{ fontSize: 60, mb: 1 }} />
            <Typography variant="h4" color="text.primary">
              Free
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              while we can 😊
            </Typography>
          </Box>

          {/* Right Side: Features List */}
          <CardContent sx={{ flexGrow: 1, p: 0, pl: { md: 4 } }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Everything you need to get started
            </Typography>

            <Grid container spacing={1}>
              {[
                "Built-in Search Engine Discoverability",
                "Customizable Links",
                "Unlimited Social Links",
                "Fast Contact Info",
                "One-Click vCard Download",
                "Instant QR Code Access",
              ].map((feature, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CheckCircleIcon sx={{ color: "success.main", mr: 1, fontSize: 20 }} />
                    <Typography variant="body2">{feature}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>




        <Grid container spacing={2} alignItems="stretch">
          {/* <Grid item xs={12} sm={6} md={4}>
                        <Suspense fallback={<CircularProgress />}>
                            <AppBundleWidget
                                title="Free"
                                amount={0}
                                reference={"Free"}
                                icon={<EmojiPeopleIcon fontSize="large" />}
                                data={[
                                    "include Social Links (Up to 5)",
                                    "include Custom Links (1)",
                                    "Media Pictures (Up to 5)",
                                    "YouTube Integration (Links to videos)",
                                    "Enhanced Biography (About)",
                                    "Analytic Tracking (View performance)",
                                    "Rating System (coupons)",
                                ]}
                                isFreeBundle={true}
                            />
                        </Suspense>
                    </Grid> */}

          <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>

            <Suspense fallback={<CircularProgress />}>
              <AppBundleWidget
                title="Student"
                amount={9}
                reference={"Student"}
                icon={<BusinessCenterIcon fontSize="large" />}
                data={[
                  "Include All Free features",
                  "Include Resume/Pitch Deck Hosting",
                  "Include AI-Powered Job Match Scoring (Up to 5 evaluations)",
                ]}
                sx={{ width: "100%" }}
                setOpenDialog={setOpenDialog}
                setReference={setReference}
                setAmount={setAmount}
                currentUserCategory={profile?.category}
                ownedBundles={ownedBundles}
                onSwitch={handleSwitch}
              />
            </Suspense>
          </Grid>

          <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
            <Suspense fallback={<CircularProgress />}>
              <AppBundleWidget
                title="Freelance"
                amount={29}
                reference={"Freelance"}
                icon={<EngineeringIcon fontSize="large" />}
                data={[
                  "Include All Student features",
                  "Include AI-Powered Job Match Scoring (+10 evaluations)",
                  "Include Seamless Contact Exchange",
                  "Include Clients Ratings & Review System",
                  "Include Performance Analytics Dashboard",
                ]}
                sx={{ width: "100%" }}
                setOpenDialog={setOpenDialog}
                setReference={setReference}
                setAmount={setAmount}
                currentUserCategory={profile?.category}
                ownedBundles={ownedBundles}
                onSwitch={handleSwitch}
              />
            </Suspense>
          </Grid>

          <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
            <Suspense fallback={<CircularProgress />}>
              <AppBundleWidget
                title="Business"
                amount={49}
                reference={"Business"}
                article={"Recommended"}
                icon={<ApartmentIcon fontSize="large" />}
                data={[
                  "Include All Freelance features",
                  "Include AI-Powered Job Match Scoring (+85 evaluations)",
                  "Include Reputation Page",
                ]}
                sx={{ width: "100%" }}
                setOpenDialog={setOpenDialog}
                setReference={setReference}
                setAmount={setAmount}
                currentUserCategory={profile?.category}
                ownedBundles={ownedBundles}
                onSwitch={handleSwitch}
              />
            </Suspense>
          </Grid>
        </Grid>


        <Suspense fallback={<CircularProgress />}>
          <ToastContainer />
        </Suspense>
      </Container>
      <Suspense fallback={<CircularProgress />}>
        <AiAdDialog />
      </Suspense>
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
        }}
      >
        <DialogTitle color="primary">
          {(() => {
            const bundleInfo = getBundleInfo(reference);
            if (bundleInfo && bundleInfo.isExpired) {
              return "Renew Bundle";
            } else {
              return "Purchase Bundle";
            }
          })()}
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              padding: "20px",
              color: "text.secondary",
            }}
          >
            {(() => {
              const bundleInfo = getBundleInfo(reference);
              if (bundleInfo && bundleInfo.isExpired) {
                return `Your ${reference} bundle has expired. Renew it for ${amount} Coins?`;
              } else {
                return `Are you sure you want to purchase ${reference} bundle for ${amount} Coins?`;
              }
            })()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handlePurchase(reference, amount);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
